import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { darken } from "polished";
import { Search as SearchIcon } from "react-feather";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useAuth from "../../hooks/useAuth";
import SearchResultsComp from "./SearchResults";

import {
  Grid,
  InputBase,
  AppBar as MuiAppBar,
  IconButton as MuiIconButton,
  Toolbar,
  Autocomplete,
  TextField,
  InputAdornment,
} from "@mui/material";

import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Home as HomeIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from "@mui/icons-material";
import NavbarUserDropdown from "./NavbarUserDropdown";
import GlobalCreate from "./GlobalCreate";
import useUser from "../../hooks/useUser";
import NavbarSearch from "./NavbarSearch";

const AppBar = styled(MuiAppBar)`
  background: #ffffff;
  color: #333333;
`;

const IconButton = styled(MuiIconButton)`
  svg {
    width: 22px;
    height: 22px;
  }
`;

const Search = styled.div`
  border-radius: 2px;
  background-color: ${(props) => props.theme.header.background};
  display: none;
  position: relative;
  width: 100%;

  &:hover {
    background-color: ${(props) => darken(0.05, props.theme.header.background)};
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
  }
`;

const currentPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [],
};

const Navbar = ({ onDrawerToggle, collapsed }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  const handleHomeClick = () => {
    navigate("/");
  };

  return (
    <React.Fragment>
      <AppBar position="sticky" elevation={0} theme={theme}>
        <Toolbar sx={{ minHeight: "46px !important", overflow: "visible" }}>
          <Grid container alignItems="center" spacing={2} sx={{ flexWrap: "nowrap" }}>
            {/* Toggle Sidebar Icon Button */}
            <Grid item>
              <IconButton
                color="inherit"
                aria-label="Toggle sidebar"
                onClick={onDrawerToggle}
                size="large"
              >
                {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
              </IconButton>
            </Grid>

            {/* Home Button - Hide on Dashboard */}
            {location.pathname !== "/" &&
              location.pathname !== "/dashboard" &&
              location.pathname !== "/dashboard/default" && (
                <Grid item>
                  <IconButton color="inherit" onClick={handleHomeClick} size="large">
                    <HomeIcon />
                  </IconButton>
                </Grid>
              )}

            {/* Global Search - Flexible space */}
            <Grid
              item
              xs
              sx={{
                display: "flex",
                justifyContent: "center",
                px: { xs: 0.5, md: 2 },
              }}
            >
              <NavbarSearch />
            </Grid>

            {/* Fullscreen Button */}
            <Grid item>
              <IconButton color="inherit" onClick={toggleFullscreen} size="large">
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Grid>

            {/* Navbar User Dropdown */}
            <Grid item>
              <NavbarUserDropdown />
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  );
};

export default Navbar;
